import type { GetIndexingJobDetailsOutput } from "@/lib/ai/tools/get-indexing-job-details/server";
import {
  buildDataset,
  buildFields,
  createTableOnlyProfile,
  isRecord,
  toNumber,
  toString,
} from "@/lib/ai/visualization/data/adapters/dataset-normalization";
import type {
  VizDataset,
  VizDatasetRow,
} from "@/lib/ai/visualization/data/schema";

const TABLE_ONLY_PROFILE = createTableOnlyProfile({
  datasetFamily: "indexing.jobDetails",
});

type JobScope = {
  workerId: string | null;
  workerName: string | null;
  job: Record<string, unknown>;
};

function collectJobScopes(output: Record<string, unknown>): JobScope[] {
  const workers = Array.isArray(output.indexWorkers) ? output.indexWorkers : [];
  const scopes: JobScope[] = [];

  for (const worker of workers) {
    if (!isRecord(worker)) continue;
    const workerId = toString(worker.id);
    const workerName = toString(worker.name);
    const jobs = isRecord(worker.jobs) ? worker.jobs : null;
    const edges = Array.isArray(jobs?.edges) ? jobs.edges : [];

    for (const edge of edges) {
      if (!isRecord(edge)) continue;
      const node = isRecord(edge.node) ? edge.node : null;
      if (!node) continue;
      scopes.push({ workerId, workerName, job: node });
    }
  }

  return scopes;
}

function buildJobsDataset(
  jobScopes: JobScope[],
  source: VizDataset["source"],
): VizDataset | null {
  const rows: VizDatasetRow[] = jobScopes.map((scope) => {
    const result = isRecord(scope.job.result) ? scope.job.result : null;
    const tasks = isRecord(scope.job.tasks) ? scope.job.tasks : null;
    const taskEdges = Array.isArray(tasks?.edges) ? tasks.edges : [];
    return {
      workerId: scope.workerId,
      workerName: scope.workerName,
      jobId: toString(scope.job.id),
      createdAt: toString(scope.job.createdAt),
      completedAt: toString(scope.job.completedAt),
      requestCount: toNumber(scope.job.requestCount),
      successCount: toNumber(scope.job.successCount),
      failureCount: toNumber(scope.job.failureCount),
      totalCount: toNumber(result?.totalCount),
      crawledCount: toNumber(result?.crawledCount),
      indexedCount: toNumber(result?.indexedCount),
      crawledRate: toNumber(result?.crawledRate),
      indexedRate: toNumber(result?.indexedRate),
      canonicalIndexedCount: toNumber(result?.canonicalIndexedCount),
      canonicalIndexedRate: toNumber(result?.canonicalIndexedRate),
      taskCount: taskEdges.filter(
        (edge) => isRecord(edge) && isRecord(edge.node),
      ).length,
    };
  });

  if (rows.length === 0) return null;

  return buildDataset({
    id: "indexing-job-summary",
    profile: TABLE_ONLY_PROFILE,
    fields: buildFields([
      { key: "workerId", role: "dim", type: "string" },
      { key: "workerName", role: "dim", type: "string" },
      { key: "jobId", role: "dim", type: "string" },
      { key: "createdAt", role: "dim", type: "string" },
      { key: "completedAt", role: "dim", type: "string" },
      { key: "requestCount", role: "metric", type: "number", unit: "count" },
      { key: "successCount", role: "metric", type: "number", unit: "count" },
      { key: "failureCount", role: "metric", type: "number", unit: "count" },
      { key: "totalCount", role: "metric", type: "number", unit: "count" },
      { key: "crawledCount", role: "metric", type: "number", unit: "count" },
      { key: "indexedCount", role: "metric", type: "number", unit: "count" },
      { key: "crawledRate", role: "metric", type: "number", unit: "percent" },
      { key: "indexedRate", role: "metric", type: "number", unit: "percent" },
      {
        key: "canonicalIndexedCount",
        role: "metric",
        type: "number",
        unit: "count",
      },
      {
        key: "canonicalIndexedRate",
        role: "metric",
        type: "number",
        unit: "percent",
      },
      { key: "taskCount", role: "metric", type: "number", unit: "count" },
    ]),
    rows,
    source,
    defaults: {
      category: "jobId",
      value: "failureCount",
    },
    notes: "Table-only job summary for indexing diagnostics.",
  });
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => toString(item))
    .filter((item): item is string => Boolean(item));
}

function toRefererText(value: unknown): string {
  const referers = toStringArray(value);
  return referers.length > 0 ? referers.join(", ") : "N/A";
}

function toUrlTableTaskRows(jobScopes: JobScope[]): VizDatasetRow[] {
  const rows: VizDatasetRow[] = [];
  for (const scope of jobScopes) {
    const tasks = isRecord(scope.job.tasks) ? scope.job.tasks : null;
    const taskEdges = Array.isArray(tasks?.edges) ? tasks.edges : [];

    for (const taskEdge of taskEdges) {
      if (!isRecord(taskEdge) || !isRecord(taskEdge.node)) continue;
      const task = taskEdge.node;
      const result = isRecord(task.result) ? task.result : null;

      rows.push({
        workerId: scope.workerId,
        workerName: scope.workerName,
        jobId: toString(scope.job.id),
        url: toString(isRecord(task.url) ? task.url.url : null),
        crawled: task.crawled === true ? "true" : "false",
        indexed: task.indexed === true ? "true" : "false",
        status: toString(result?.coverageState) ?? "N/A",
        isFailure: "false",
        userCanonical: toString(result?.userCanonical) ?? "N/A",
        googleCanonical: toString(result?.googleCanonical) ?? "N/A",
        crawledAs: toString(result?.crawledAs) ?? "N/A",
        lastCrawledAt: toString(result?.lastCrawlTime) ?? "N/A",
        referer: toRefererText(result?.refferingUrls),
      });
    }
  }
  return rows;
}

function toUrlTableFailureRows(jobScopes: JobScope[]): VizDatasetRow[] {
  const rows: VizDatasetRow[] = [];
  for (const scope of jobScopes) {
    const failureUrls = Array.isArray(scope.job.failureURLs)
      ? scope.job.failureURLs
      : [];
    for (const failure of failureUrls) {
      if (!isRecord(failure)) continue;
      rows.push({
        workerId: scope.workerId,
        workerName: scope.workerName,
        jobId: toString(scope.job.id),
        url: toString(failure.url),
        crawled: "false",
        indexed: "false",
        status: toString(failure.error) ?? "N/A",
        isFailure: "true",
        userCanonical: "N/A",
        googleCanonical: "N/A",
        crawledAs: "N/A",
        lastCrawledAt: "N/A",
        referer: "N/A",
      });
    }
  }
  return rows;
}

function buildUrlsDataset(
  jobScopes: JobScope[],
  source: VizDataset["source"],
): VizDataset | null {
  const rows = [
    ...toUrlTableTaskRows(jobScopes),
    ...toUrlTableFailureRows(jobScopes),
  ];

  if (rows.length === 0) return null;

  return buildDataset({
    id: "indexing-job-urls",
    profile: TABLE_ONLY_PROFILE,
    fields: buildFields([
      { key: "workerId", role: "dim", type: "string" },
      { key: "workerName", role: "dim", type: "string" },
      { key: "jobId", role: "dim", type: "string" },
      { key: "url", role: "dim", type: "string" },
      { key: "crawled", role: "dim", type: "string" },
      { key: "indexed", role: "dim", type: "string" },
      { key: "status", role: "dim", type: "string" },
      { key: "isFailure", role: "dim", type: "string" },
      { key: "userCanonical", role: "dim", type: "string" },
      { key: "googleCanonical", role: "dim", type: "string" },
      { key: "crawledAs", role: "dim", type: "string" },
      { key: "lastCrawledAt", role: "dim", type: "string" },
      { key: "referer", role: "dim", type: "string" },
    ]),
    rows,
    source,
    defaults: { category: "url" },
    notes:
      "URLs table aligned with Index Worker detail UI (tasks + failure URLs). Charting is disabled for this diagnostic dataset.",
  });
}

/**
 * Index Worker job detail 出力を diagnostics 用 table dataset 群へ変換する。
 */
export function buildIndexingJobDetailsDatasets(
  rawOutput: unknown,
  source: VizDataset["source"],
): VizDataset[] {
  // adapter 契約外の payload（null/非 object）はここで停止して誤推論を防ぐ。
  if (typeof rawOutput !== "object" || rawOutput === null) return [];
  const output = rawOutput as Partial<GetIndexingJobDetailsOutput> &
    Record<string, unknown>;

  const jobScopes = collectJobScopes(output);
  if (jobScopes.length === 0) return [];

  const datasets = [
    buildUrlsDataset(jobScopes, source),
    buildJobsDataset(jobScopes, source),
  ].filter((dataset): dataset is VizDataset => Boolean(dataset));

  return datasets;
}
